import * as React from "react"
import { HorizontalSpacer } from "../atoms/spacers"
import styled from "styled-components"
import H1 from "../atoms/typography/H1"
import { theme } from "../theme"
import { Col, Row } from "react-grid-system"
import { ActiveItemBar } from "../atoms/ActiveItemColoredBar"
import H3 from "../atoms/typography/H3"
import H2 from "../atoms/typography/H2"
import Body from "../atoms/typography/Body"

export interface UspData {
  title: string
  label: string
  imagesCollection: {
    items: Array<{
      title: string
      description: string
      url: string
    }>
  }
  content: {
    json: {
      nodeType: string
      data: Record<string, unknown>
      content: Array<{
        nodeType: string
        data: Record<string, unknown>
        content: Array<{
          nodeType: string
          value?: string
          marks: Array<unknown>
          data: Record<string, unknown>
        }>
      }>
    }
  }
}

export interface UspCarouselData {
  color: "yellow" | "blue" | "green" | "red"
  uspsCollection: {
    items: Array<UspData>
  }
}

const Title = styled(H1)<{ gradient: string }>`
  background: ${(props) => props.gradient};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
`

interface UspCarouselContentProps {
  textColor?: "white" | "dark"
  color: "yellow" | "blue" | "green" | "red"
  usp: UspData
}

const UspCarouselContent = (props: UspCarouselContentProps) => {
  const content = props.usp.content.json.content

  const headings = content.filter((block) => block.nodeType === "heading-1")
  const paragraphs = content.filter((block) => block.nodeType === "paragraph")
  const images = props.usp.imagesCollection.items

  const entries = headings.map((heading, index) => ({
    heading: heading.content[0]?.value || "",
    paragraph: paragraphs[index]?.content[0]?.value || "",
    image: images[index]?.url || "",
  }))

  return (
    <Col>
      <Row>
        <Col>
          <Title gradient={theme.gradients[props.color]}>{props.usp.title}</Title>
          <HorizontalSpacer spacing={1} />
        </Col>
      </Row>
      {entries.map((entry, index) => (
        <Row key={index} align="center">
          <Col xs="content">
            <HorizontalSpacer spacing={1} />
            <img src={entry.image} alt={entry.heading} />
            <HorizontalSpacer spacing={1} />
          </Col>
          <Col>
            <H2 color={theme.colors[props.textColor!]}>{entry.heading}</H2>
            <Body color={theme.colors[props.textColor!]}>{entry.paragraph}</Body>
          </Col>
        </Row>
      ))}
    </Col>
  )
}

interface UspCarouseSelectorProps {
  usp: UspData
  color: "yellow" | "blue" | "green" | "red"
  textColor?: "white" | "dark"
  index: number
  activeIndex: number
  handleClick: (index: number) => void
}

const UspCarouselSelector: React.FC<UspCarouseSelectorProps> = ({
  usp,
  color,
  index,
  activeIndex,
  handleClick,
  textColor,
}) => {
  return (
    <Col xs={6} onClick={() => handleClick(index)} key={index}>
      <Row justify="center" align="center" style={{ cursor: "pointer" }}>
        <H3 color={theme.colors[textColor!]}>{usp.label}</H3>
        <HorizontalSpacer spacing={2} />
        <ActiveItemBar color={index === activeIndex ? theme.gradients[color] : theme.colors.greyDark} />
      </Row>
      <HorizontalSpacer spacing={4} />
    </Col>
  )
}

interface UspCarouselProps {
  uspCarousel: UspCarouselData
  textColor?: "white" | "dark"
}

const UspCarouselTwo: React.FC<UspCarouselProps> = ({ uspCarousel, textColor = "white" }) => {
  const [activeIndex, setActiveIndex] = React.useState(0)

  const handleClick = (index: number) => {
    setActiveIndex(index)
  }

  const usps = uspCarousel.uspsCollection.items
  const uspColor = uspCarousel.color || "blue"

  return (
    <Row align="center" justify="center">
      <Col lg={6}>
        <Row direction="column" align="center" justify="center">
          {usps.map((usp, index) => (
            <UspCarouselSelector
              key={index}
              color={uspColor}
              textColor={textColor}
              usp={usp}
              index={index}
              activeIndex={activeIndex}
              handleClick={handleClick}
            />
          ))}
        </Row>
      </Col>
      <Col lg={6}>
        <UspCarouselContent textColor={textColor} usp={usps[activeIndex]} color={uspColor} />
      </Col>
    </Row>
  )
}

export default UspCarouselTwo
